import TextField from '@mui/material/TextField';
import React, { useEffect } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha } from '@mui/material/styles';
import COLORS from '../../constants/colors';
import { CopyToClipboardButton } from '../CopyToClipboardButton';
import { IAsyncDialogProps } from './AsyncDialog';

export type ExecuteWarningDialogData = {
  orderId: string;
};

/**
 * The manual Execute button only moves the order's status in portfolio. Alpaca, Derayah and jadwa place the real
 * fund-manager order and then call that same execute themselves — and they only pick up orders still CREATED, so a
 * manual execute takes the order out of their reach for good.
 */
const CONSEQUENCES: { title: string; text: string }[] = [
  {
    title: 'No ETF or mutual fund order is created',
    text: 'at Alpaca, Derayah, Jadwa, Albilad or SNB.',
  },
  {
    title: 'The fund manager can never execute it after this.',
    text: 'They only pick up orders that are still Created, and this order won’t be.',
  },
  {
    title: 'The ledger skips the fund manager’s journals:',
    text: 'the transfer (send / received) and the FX. The cash pools will not balance.',
  },
  {
    title: 'It can’t be undone from this screen.',
    text: '',
  },
];

export function ExecuteWarningDialog(props: IAsyncDialogProps<ExecuteWarningDialogData, boolean>) {
  const [confirmationValue, setConfirmationValue] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const orderId = props.data?.orderId;
  const isMatch = Boolean(orderId) && confirmationValue === orderId;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTouched(true);
    setConfirmationValue(event.target.value);
  };

  const onConfirm = () => {
    if (isMatch) {
      props.onOk(true);
    }
  };

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Enter') {
      onConfirm();
    }
  };

  useEffect(() => {
    if (!props.open) {
      setConfirmationValue('');
      setTouched(false);
    }
  }, [props.open]);

  return (
    <Dialog open={props.open} onClose={() => props.onOk(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Execute this order manually?
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning">
          <AlertTitle sx={{ fontWeight: 600 }}>This only changes the order’s status in Madkhol</AlertTitle>
          It does not send anything to the fund manager. Use it only if you know exactly why you need it.
        </Alert>

        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0 }}>
          {CONSEQUENCES.map((item) => (
            <Box
              component="li"
              key={item.title}
              sx={{
                display: 'flex',
                gap: 1.25,
                py: 1,
                borderBottom: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Typography component="span" sx={{ color: COLORS.RED, fontWeight: 700, width: 16, flex: 'none' }}>
                ✕
              </Typography>
              <Typography component="span" variant="body2" sx={{ color: COLORS.DARK_GREY }}>
                <Box component="b" sx={{ color: COLORS.MAIN_DARK, fontWeight: 600 }}>
                  {item.title}
                </Box>{' '}
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>

        <DialogContentText sx={{ mt: 2 }}>
          If you’re sure, type the order id &nbsp;
          <CopyToClipboardButton value={orderId ?? ''} />
          &nbsp; to confirm.
        </DialogContentText>
        <TextField
          id="execute-confirmation-value"
          fullWidth
          name="confirmation value"
          label="Confirmation"
          placeholder={orderId}
          value={confirmationValue}
          onChange={handleChange}
          onKeyDown={keyDown}
          margin="normal"
          error={touched && !isMatch}
          helperText={touched && !isMatch && `Please enter "${orderId}" to confirm the operation`}
          required
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.onOk(false)}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={!isMatch}>
          Execute anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
